'use client';

import type { ProductDetailVersionResponse } from 'src/types/product-rankings/product-rankings-ops';

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { marketKeys } from 'src/query-keys/market';
import { fetchProductDetailVersion } from 'src/actions/els/product-rankings';

import { cacheKey, runCached } from '../../../utils/market-rankings-request-cache';

export type ProductDetailVersionState = {
  loading: boolean;
  error: string | null;
  data: Extract<ProductDetailVersionResponse, { found: true }> | null;
  /** 물어봤는데 **그 시점 모습이 남아 있지 않다** — 지문 도입 이전 수집분 */
  missing: boolean;
};

const EMPTY: ProductDetailVersionState = {
  loading: false,
  error: null,
  data: null,
  missing: false,
};

/**
 * 타임라인에서 고른 **옛 버전 한 판**을 읽는다.
 *
 * ⚠️ `seq`가 `null`이면 **현재 버전을 보고 있다는 뜻**이라 아무것도 안 부른다. 현재 상세는
 * 이미 `rankings.detail`이 통째로 들고 있어서, 같은 것을 한 번 더 물으면 왕복만 는다.
 *
 * ⚠️ 옛 버전은 **불변이다.** 이미 지나간 버전의 미디어·본문은 다시 바뀌지 않으므로
 * `staleTime`을 길게 준다 — 상세(60초)와 다른 값인 것이 의도다.
 *
 * ⚠️ 「없음」과 「실패」를 가른다. 버전 문서가 없는 상품은 200에 `found: false`로 오고,
 * 그건 에러가 아니라 **안내할 사실**이다(`missing`).
 */
export function useProductDetailVersion(
  ref: { postId?: string; source?: string } | null | undefined,
  seq: number | null,
): ProductDetailVersionState {
  const postId = ref?.postId || '';
  const source = ref?.source || '';
  const enabled = Boolean(postId && seq != null && seq >= 1);

  const params = useMemo(
    () => ({ postId, source, seq: seq ?? 0 }),
    [postId, source, seq],
  );
  const key = useMemo(() => cacheKey('rankings.detailVersion', params), [params]);

  const query = useQuery({
    queryKey: marketKeys.detailVersion(key),
    queryFn: () => runCached(key, () => fetchProductDetailVersion(params)),
    enabled,
    // 지나간 버전은 다시 바뀌지 않는다 (머리말)
    staleTime: 30 * 60_000,
  });

  return useMemo<ProductDetailVersionState>(() => {
    if (!enabled) return EMPTY;
    if (query.isError) {
      const failure = query.error as { message?: unknown };
      return {
        loading: false,
        error:
          typeof failure?.message === 'string' && failure.message
            ? failure.message
            : '이 버전의 상세페이지를 불러오지 못했습니다.',
        data: null,
        missing: false,
      };
    }
    if (!query.data) return { ...EMPTY, loading: true };
    if (!query.data.found) return { loading: false, error: null, data: null, missing: true };
    return { loading: false, error: null, data: query.data, missing: false };
  }, [enabled, query.isError, query.error, query.data]);
}

export default useProductDetailVersion;
